import { useCallback, useLayoutEffect, useRef } from 'react'

// Drops concurrent invocations via a ref-based gate (state-based `loading`/`disabled`
// flips only take effect after a render, leaving a window for a rapid second click).
// The handler ref is written in a layout effect so concurrent renders that get
// discarded don't leave the ref pointing at an uncommitted handler.
export const usePreventDoubleSubmit = <Args extends unknown[], R>(
  handler: (...args: Args) => Promise<R> | R
): ((...args: Args) => Promise<R | undefined>) => {
  const inFlightRef = useRef<boolean>(false)
  const handlerRef = useRef(handler)

  useLayoutEffect(() => {
    handlerRef.current = handler
  })

  return useCallback(async (...args: Args): Promise<R | undefined> => {
    if (inFlightRef.current) return undefined
    inFlightRef.current = true
    try {
      return await handlerRef.current(...args)
    } finally {
      inFlightRef.current = false
    }
  }, [])
}
